import React, { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Save, Trophy, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  gamesAdminApi,
  type LeaderboardRow,
  type SparringPartnerRow,
  type SparringProfile,
  type SparringProfileStats,
  type SparringSettings,
} from '../../api/games';
import {
  Card,
  InfoHint,
  NumberField,
  SaveButton,
  Stat,
  TabIntro,
  TextField,
  Toggle,
} from './controls';

/**
 * Вкладка «Спарринг» — управление искусственными соперниками в дуэлях.
 * Сила настраивается пятью профилями, имена живут отдельным пулом, поэтому
 * игрок видит в истории разные ники. См. ADR 0018.
 */
export default function SparringTab({ isDark }: { isDark: boolean }) {
  const [settings, setSettings] = useState<SparringSettings | null>(null);
  const [profiles, setProfiles] = useState<SparringProfile[]>([]);
  const [partners, setPartners] = useState<SparringPartnerRow[]>([]);
  const [stats, setStats] = useState<SparringProfileStats[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // allSettled, а не all: упавший лидерборд не должен прятать профили и
      // настройки — вкладка обязана оставаться рабочей по частям.
      const [s, p, allPartners, st, lb] = await Promise.allSettled([
        gamesAdminApi.sparringSettings(),
        gamesAdminApi.sparringProfiles(),
        gamesAdminApi.sparringPartners(),
        gamesAdminApi.sparringStats(),
        gamesAdminApi.duelLeaderboard('all'),
      ]);

      if (s.status === 'fulfilled') setSettings(s.value);
      if (p.status === 'fulfilled') setProfiles(p.value);
      if (allPartners.status === 'fulfilled') setPartners(allPartners.value);
      if (st.status === 'fulfilled') setStats(st.value);
      if (lb.status === 'fulfilled') setLeaderboard(lb.value.rows ?? []);

      const failed = [s, p, allPartners, st, lb].filter(
        (r) => r.status === 'rejected',
      );
      if (failed.length) {
        setError(`Не удалось загрузить часть данных (${failed.length} из 5)`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <TabIntro isDark={isDark}>
        Дуэль требует двух игроков, но живой соперник находится далеко не всегда.
        Чтобы человек не играл в пустоту, через несколько секунд поиска ему
        подсаживается <b>спарринг-партнёр</b> — искусственный соперник. В
        приложении он выглядит обычным игроком и ничем не помечен.
        <br />
        <br />
        Сила задаётся <b>профилем</b> (их пять, по одному на ранг), а имя берётся
        из общего пула — поэтому в истории дуэлей игрок видит разные ники, а
        настраивать нужно всего пять карточек. Рейтинг партнёра — константа: он
        не растёт и не падает от матчей и никогда не попадает в лидерборд.
      </TabIntro>

      <div className="flex items-center justify-end">
        <button
          onClick={load}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition',
            isDark
              ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
              : 'border-slate-200 text-slate-700 hover:bg-slate-100',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Обновить
        </button>
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-sm bg-red-500/10 text-red-500 border border-red-500/30">
          {error}
        </div>
      )}

      {settings && (
        <SettingsCard
          isDark={isDark}
          settings={settings}
          onSaved={(next) => setSettings(next)}
        />
      )}

      <div className="space-y-4">
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            isDark={isDark}
            profile={profile}
            stats={stats.find((s) => s.profileId === profile.id) ?? null}
            partners={partners.filter((p) => p.profileId === profile.id)}
            onProfileSaved={(next) =>
              setProfiles((prev) =>
                prev.map((p) => (p.id === next.id ? next : p)),
              )
            }
            onPartnerAdded={(partner) =>
              setPartners((prev) => [...prev, partner])
            }
            onPartnerRetired={(id) =>
              setPartners((prev) => prev.filter((p) => p.id !== id))
            }
          />
        ))}
      </div>

      <LeaderboardCard isDark={isDark} rows={leaderboard} onRefresh={load} />
    </div>
  );
}

// ─── Настройки фичи ─────────────────────────────────────────────────────────

function SettingsCard({
  isDark,
  settings,
  onSaved,
}: {
  isDark: boolean;
  settings: SparringSettings;
  onSaved: (next: SparringSettings) => void;
}) {
  const [draft, setDraft] = useState<SparringSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => setDraft(settings), [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const next = await gamesAdminApi.updateSparringSettings(draft);
      onSaved(next);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card isDark={isDark} title="Настройки">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Toggle
          isDark={isDark}
          label="Спарринг включён"
          hint="Главный рубильник. Выключишь — дуэли вернутся к прежнему поведению: игрок сыграет и будет ждать живого соперника до суток."
          value={draft.enabled}
          onChange={(enabled) => setDraft({ ...draft, enabled })}
        />
        <NumberField
          isDark={isDark}
          label="Окно поиска живого, мс"
          hint="Сколько секунд держим игрока в поиске, давая шанс встретиться с живым человеком. Всё это время он видит спиннер, поэтому больше 5 секунд ставить не стоит. Живой соперник всегда в приоритете."
          value={draft.search_window_ms}
          min={0}
          max={15000}
          onChange={(search_window_ms) => setDraft({ ...draft, search_window_ms })}
        />
        <NumberField
          isDark={isDark}
          label="Рейтинговых дуэлей с партнёром в сутки"
          hint="Сколько матчей против партнёров за день меняют рейтинг игрока. Дальше матчи идут как тренировка: результат показывается, ELO стоит. Защищает лидерборд от премиум-игроков, которые могли бы фармить рейтинг об ботов без ограничений."
          value={draft.rated_cap_per_day}
          min={0}
          max={50}
          onChange={(rated_cap_per_day) =>
            setDraft({ ...draft, rated_cap_per_day })
          }
        />
        <Toggle
          isDark={isDark}
          label="Добивать зависшие матчи"
          hint="Если игрок сыграл, а соперник так и не пришёл, партнёр доигрывает за него: матч закрывается, человек получает пуш и возвращается посмотреть результат."
          value={draft.backfill_enabled}
          onChange={(backfill_enabled) =>
            setDraft({ ...draft, backfill_enabled })
          }
        />
        <NumberField
          isDark={isDark}
          label="Добивать через, мин"
          hint="Сколько ждать живого соперника, прежде чем матч добьёт партнёр. Слишком мало — задавим живой PvP, слишком много — человек забудет, что вообще играл."
          value={draft.backfill_after_minutes}
          min={1}
          max={1440}
          onChange={(backfill_after_minutes) =>
            setDraft({ ...draft, backfill_after_minutes })
          }
        />
        <NumberField
          isDark={isDark}
          label="Матчей за один прогон"
          hint="Крон добивает матчи пачками каждые 5 минут. Это ограничение не даёт разослать сотни пушей одновременно."
          value={draft.backfill_batch_size}
          min={1}
          max={500}
          onChange={(backfill_batch_size) =>
            setDraft({ ...draft, backfill_batch_size })
          }
        />
      </div>

      <div className="flex items-center gap-3 mt-5">
        <SaveButton isDark={isDark} saving={saving} onClick={save} />
        {savedAt && (
          <span className="text-xs text-emerald-500">Сохранено</span>
        )}
      </div>
    </Card>
  );
}

// ─── Профиль сложности ──────────────────────────────────────────────────────

function ProfileCard({
  isDark,
  profile,
  stats,
  partners,
  onProfileSaved,
  onPartnerAdded,
  onPartnerRetired,
}: {
  isDark: boolean;
  profile: SparringProfile;
  stats: SparringProfileStats | null;
  partners: SparringPartnerRow[];
  onProfileSaved: (next: SparringProfile) => void;
  onPartnerAdded: (partner: SparringPartnerRow) => void;
  onPartnerRetired: (id: string) => void;
}) {
  const [draft, setDraft] = useState<SparringProfile>(profile);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => setDraft(profile), [profile]);

  const save = async () => {
    setSaving(true);
    try {
      const next = await gamesAdminApi.updateSparringProfile(profile.id, {
        title: draft.title,
        elo_rating: draft.elo_rating,
        accuracy: draft.accuracy,
        tricky_penalty: draft.tricky_penalty,
        min_answer_ms: draft.min_answer_ms,
        max_answer_ms: draft.max_answer_ms,
        weight: draft.weight,
        is_active: draft.is_active,
      });
      onProfileSaved(next);
    } finally {
      setSaving(false);
    }
  };

  const addPartner = async () => {
    const name = newName.trim();
    if (!name) return;
    setAddError(null);
    try {
      const created = await gamesAdminApi.addSparringPartner(profile.id, name);
      onPartnerAdded(created);
      setNewName('');
    } catch (err: any) {
      setAddError(err?.response?.data?.message || 'Не удалось добавить');
    }
  };

  const retire = async (id: string) => {
    await gamesAdminApi.retireSparringPartner(id);
    onPartnerRetired(id);
  };

  // Целевой коридор — 55–60% побед игрока. Отклонение подсвечиваем, чтобы
  // калибровать точность профиля по факту, а не на глаз.
  const winRate = stats?.userWinRate ?? 0;
  const winRateTone =
    !stats || stats.matches < 20
      ? 'slate'
      : winRate >= 50 && winRate <= 65
        ? 'emerald'
        : 'red';

  return (
    <Card
      isDark={isDark}
      title={`${draft.title} · ${draft.code}`}
      dimmed={!draft.is_active}
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TextField
              isDark={isDark}
              label="Название"
              value={draft.title}
              onChange={(title) => setDraft({ ...draft, title })}
            />
            <NumberField
              isDark={isDark}
              label="ELO"
              hint="Рейтинг партнёра. Определяет, каким игрокам он попадётся: подбор идёт в пределах своего ранга и соседних. Матчи это число не меняют."
              value={draft.elo_rating}
              min={0}
              max={3000}
              onChange={(elo_rating) => setDraft({ ...draft, elo_rating })}
            />
            <NumberField
              isDark={isDark}
              label="Точность, %"
              hint="Вероятность верного ответа. Главный рычаг сложности: 50% — слабый соперник, 90% — почти безошибочный. Ориентир: игроки должны выигрывать 55–60% матчей."
              value={Math.round(draft.accuracy * 100)}
              min={5}
              max={100}
              onChange={(v) => setDraft({ ...draft, accuracy: v / 100 })}
            />
            <NumberField
              isDark={isDark}
              label="Штраф на хитрых, %"
              hint="Насколько падает точность на вопросах с пометкой «хитрый». Благодаря этому партнёр ошибается там же, где ошибаются живые люди, а не случайно."
              value={Math.round(draft.tricky_penalty * 100)}
              min={0}
              max={60}
              onChange={(v) => setDraft({ ...draft, tricky_penalty: v / 100 })}
            />
            <NumberField
              isDark={isDark}
              label="Мин. время ответа, мс"
              hint="Нижняя граница раздумий над одним вопросом. Слишком мало — выдаёт бота: человек не отвечает за секунду. 1000 мс = 1 секунда."
              value={draft.min_answer_ms}
              min={500}
              max={60000}
              onChange={(min_answer_ms) => setDraft({ ...draft, min_answer_ms })}
            />
            <NumberField
              isDark={isDark}
              label="Макс. время ответа, мс"
              hint="Верхняя граница раздумий. Общее время складывается из десяти таких интервалов и решает исход при равном счёте — кто быстрее, тот и выиграл."
              value={draft.max_answer_ms}
              min={500}
              max={120000}
              onChange={(max_answer_ms) => setDraft({ ...draft, max_answer_ms })}
            />
            <NumberField
              isDark={isDark}
              label="Вес в подборе"
              hint="Насколько часто этот профиль выпадает среди подходящих по рангу. Больше вес — чаще встречается."
              value={draft.weight}
              min={1}
              max={10}
              onChange={(weight) => setDraft({ ...draft, weight })}
            />
            <Toggle
              isDark={isDark}
              label="Активен"
              hint="Выключенный профиль не участвует в подборе, но его имена и история матчей сохраняются."
              value={draft.is_active}
              onChange={(is_active) => setDraft({ ...draft, is_active })}
            />
          </div>

          <SaveButton isDark={isDark} saving={saving} onClick={save} />

          <div>
            <div
              className={cn(
                'text-xs font-medium mb-2',
                isDark ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              Имена ({partners.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {partners.map((p) => (
                <span
                  key={p.id}
                  className={cn(
                    'inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-sm border',
                    isDark
                      ? 'bg-slate-900 border-slate-700 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700',
                  )}
                >
                  {p.gamerTag}
                  <button
                    onClick={() => retire(p.id)}
                    title="Вывести из подбора (история сохранится)"
                    className={cn(
                      'w-5 h-5 rounded-full flex items-center justify-center transition',
                      isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-200',
                    )}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addPartner()}
                placeholder="Новое имя"
                maxLength={20}
                className={cn(
                  'px-3 py-2 rounded-lg text-sm border w-48 outline-none',
                  isDark
                    ? 'bg-slate-900 border-slate-700 text-white'
                    : 'bg-white border-slate-200 text-slate-900',
                )}
              />
              <button
                onClick={addPartner}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-[#C9A227] text-white"
              >
                <Plus className="w-4 h-4" />
                Добавить
              </button>
              {addError && (
                <span className="text-xs text-red-500">{addError}</span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Stat
            isDark={isDark}
            label="Матчей"
            value={String(stats?.matches ?? 0)}
            tooltip="Сколько дуэлей против партнёров этого профиля уже сыграно. Пока матчей меньше 20, проценту побед верить рано."
          />
          <Stat
            isDark={isDark}
            label="Побед игроков"
            value={`${winRate}%`}
            tone={winRateTone}
            tooltip="Как часто живые игроки побеждают этот профиль. Зелёный — норма. Сильно выше коридора означает, что партнёр слаб и рейтинги поедут вверх; сильно ниже — слишком силён и это демотивирует. Правится полем «Точность»."
            hint={
              stats && stats.matches < 20
                ? 'мало данных'
                : 'целевой коридор 55–60%'
            }
          />
          <Stat
            isDark={isDark}
            tooltip="Средний счёт из 10 вопросов: слева живые игроки, справа партнёры этого профиля. Удобно для тонкой настройки точности."
            label="Счёт игрок / партнёр"
            value={`${(stats?.avgUserScore ?? 0).toFixed(1)} : ${(
              stats?.avgPartnerScore ?? 0
            ).toFixed(1)}`}
          />
          <Stat
            isDark={isDark}
            tooltip="Итоги всех матчей против этого профиля глазами живого игрока."
            label="Победы / поражения / ничьи"
            value={`${stats?.userWins ?? 0} / ${stats?.userLosses ?? 0} / ${
              stats?.draws ?? 0
            }`}
          />
        </div>
      </div>
    </Card>
  );
}

// ─── Лидерборд ──────────────────────────────────────────────────────────────

function LeaderboardCard({
  isDark,
  rows,
  onRefresh,
}: {
  isDark: boolean;
  rows: LeaderboardRow[];
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const rebuild = async () => {
    setBusy(true);
    try {
      await gamesAdminApi.refreshLeaderboards();
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      isDark={isDark}
      title="Лидерборд дуэлей — то, что видят игроки"
      icon={<Trophy className="w-5 h-5" />}
    >
      {rows.length === 0 ? (
        <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Пока пусто — ни одной завершённой рейтинговой дуэли.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                className={cn(
                  'text-left',
                  isDark ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Игрок</th>
                <th className="py-2 pr-4 font-medium">ELO</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((row) => (
                <tr
                  key={row.userId}
                  className={cn(
                    'border-t',
                    isDark ? 'border-slate-800' : 'border-slate-100',
                  )}
                >
                  <td className="py-2 pr-4">{row.rank}</td>
                  <td
                    className={cn(
                      'py-2 pr-4',
                      isDark ? 'text-white' : 'text-slate-900',
                    )}
                  >
                    {row.gamerTag}
                  </td>
                  <td className="py-2 pr-4">{row.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
          В таблицу попадают только живые игроки: спарринг-партнёры,
          заблокированные и удалившие аккаунт исключены. Данные кэшируются до
          часа — после блокировки читера жми «Пересобрать», чтобы увидеть
          результат сразу.
        </p>
        <button
          onClick={rebuild}
          disabled={busy}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition shrink-0 disabled:opacity-50',
            isDark
              ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
              : 'border-slate-200 text-slate-700 hover:bg-slate-100',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', busy && 'animate-spin')} />
          {busy ? 'Пересобираю…' : 'Пересобрать'}
        </button>
      </div>
    </Card>
  );
}
